/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import _has from "lodash/has";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import { getProductData } from "../productService";
import _includes from "lodash/includes";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromIban,
  fromInsurer,
  fromNumber,
  fromUserInputtedFloat,
  getConvertedFormDataBase,
  persistAdditionalClients,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizeOrganizationEmailData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toInsurer,
  toNumber,
  toUserInputtedFloat,
} from "../formServiceFunctions";
import ApiClient from "../apiClient";
import _isEmpty from "lodash/isEmpty";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";
import { isCurrentFlexServiceProduct, isFlexServiceProduct } from "../advocardService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY]: {
      [product.name]: [],
    },

    [applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]: {
      [product.name]: [
        "caseCategory",
        "affectedPartyType",
        "clientWasMinorAtContractDate",
        "contractDate",
        "objectiveClient",
        "subjectOfDispute",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "claimedAmountClient",
        "overdueFees",
        "defenseProcessStatus",
        "paymentOrderDate",
        "enforcementOrderDate",
        "contractualPartnerHasLawyer",
        "statementOfClaimFiledDate",
        "clientLegalRepresentativeAmount",
        "clientLegalRepresentativesAuthorizationType",
      ],
      "opponentLawFirm.organization": ["name", "telephone"],
      "opponentLawFirm.organization.mainEmail": ["email"],
      "opponentLawFirm.organization.address": ["streetAddress", "postalCode", "addressLocality"],
      opponentLawyer: ["gender", "givenName", "familyName"],
      "client.bankData": ["iban"],
      client: ["gender", "givenName", "familyName", "birthDate"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      clientLegalRepresentative: ["gender", "givenName", "familyName"],
      "clientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      secondClientLegalRepresentative: ["gender", "givenName", "familyName"],
      "secondClientLegalRepresentative.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION]: {
      [product.name]: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]: {
      product: ["contractNumber", "buyerPaidStatus", "buyerPaidDate", "buyerPaidAmount"],
      client: ["birthDate"],
      "customer.person": ["birthDate"],
      travelContract: ["reasonForDispute", "bookingDate", "journeyStart", "journeyEnd", "contractualPartnerHasCancelled", "contractualPartnerCancellationDate"],
      purchaseContract: [
        "buyerOrSeller",
        "channel",
        "itemTitle",
        "articleNumber",
        "purchasePrice",
        "goodsOrServiceProvidedStatus",
        "locationOfGood",
        "hasDefects",
        "defectsDescriptionClient",
        "numberRepairAttempts",
        "numberReplacementAttempts",
      ],
      vehicleContract: [
        "buyerOrSeller",
        "isNewVehicle",
        "vehicleMake",
        "model",
        "vehicleId",
        "purchasePrice",
        "mileageAtPurchase",
        "currentMileage",
        "isThirdPartyFinanced",
        "locationOfGood",
        "hasDefects",
        "isAccidentCar",
        "defectsRecognitionDate",
        "defectsDescriptionClient",
        "goodsOrServiceProvidedStatus",
        "numberRepairAttempts",
        "numberReplacementAttempts",
      ],
      subscriptionContract: ["wasDeceived", "salesChannel", "subscriptionName"],
      membershipContract: [
        "membershipType",
        "reasonForDispute",
        "contractNumber",
        "cancellationDate",
        "endOfContractDate",
        "feePaymentFrequency",
        "fee",
        "numberUnpaidPeriods",
        "priceIncreaseAnnouncementDate",
        "endOfContractDateOpponent",
        "opponentReactionToCancellation",
      ],
      serviceContract: [
        "reasonForDispute",
        "serviceTitle",
        "costEstimate",
        "costEstimateAdjustment",
        "costEstimateAdjustmentDate",
        "costReal",
        "defectsDescriptionClient",
        "numberRepairAttempts",
      ],
      eventContract: ["reasonForDispute", "eventType", "contractNumber", "eventTitle", "eventCity", "eventDate", "numberOfTickets"],
      providerContract: [
        "providerType",
        "reasonForDispute",
        "cancellationReceivedDate",
        "priceIncreaseAnnouncementDate",
        "cancellationDate",
        "endOfContractDate",
        "contractNumber",
        "opponentReactionToCancellation",
        "endOfContractDateOpponent",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]: {
      [product.name]: ["opposingPartyType"],
      "opposingPartyLegalEntity.organization": ["legalName", "telephone"],
      "opposingPartyLegalEntity.organization.mainEmail": ["email"],
      "opposingPartyLegalEntity.organization.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]: {
      [product.name]: ["debtCollectionAgencyReference"],
      "debtCollectionAgency.organization": ["name", "telephone"],
      "debtCollectionAgency.organization.mainEmail": ["email"],
      "debtCollectionAgency.organization.address": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
      [product.name]: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
      [product.name]: ["missingDocuments"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      [product.name]: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "client.bankData.iban",
      convertFrom: fromIban,
      convertTo: toIban,
    },
    {
      path: "contractualPartnerHasLawyer",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "contractDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "eventDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "numberOfTickets",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "statementOfClaimFiledDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "costEstimateAdjustmentDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "paymentOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "enforcementOrderDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientHasAlreadyClaimed",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "clientWasMinorAtContractDate",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "wasDeceived",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedDateClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "claimedDeadlineClient",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "cancellationDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "cancellationReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "priceIncreaseAnnouncementDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "endOfContractDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "endOfContractDateOpponent",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "claimedAmountClient",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "overdueFees",
      convertFrom: fromFloat,
      convertTo: (value: string | null) => (value === null ? 0 : toFloat(value)),
    },
    {
      path: "caseInsurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberRepairAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "costEstimate",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "costReal",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "fee",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberUnpaidPeriods",
      convertFrom: fromUserInputtedFloat,
      convertTo: (value: string | null) => {
        if (!value) {
          return 0;
        }
        return toUserInputtedFloat(value);
      },
    },
    {
      path: "buyerPaidDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "bookingDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "journeyStart",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "journeyEnd",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "contractualPartnerCancellationDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "contractualPartnerHasCancelled",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "buyerPaidAmount",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "purchasePrice",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "mileageAtPurchase",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "currentMileage",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "hasDefects",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "isAccidentCar",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "numberRepairAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "numberReplacementAttempts",
      convertFrom: fromUserInputtedFloat,
      convertTo: toUserInputtedFloat,
    },
    {
      path: "defectsRecognitionDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "clientLegalRepresentativeAmount",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
    {
      path: "isNewVehicle",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "isThirdPartyFinanced",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};

    let hasCustomerUpdates = false;

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      hasCustomerUpdates = true;
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = _includes(["customer", "unknown"], formData.affectedPartyType)
        ? formData.customer.person.residenceAddress
        : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = _includes(["customer", "unknown"], convertedFormData.affectedPartyType)
        ? convertedFormData.customer.person
        : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "clientLegalRepresentative")) {
      let clientLegalRepresentativeData = pickAndConvert(formData.clientLegalRepresentative, fields.clientLegalRepresentative);
      clientLegalRepresentativeData = sanitizePersonData(clientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.clientLegalRepresentative.id,
        data: clientLegalRepresentativeData,
      });
    }

    if (_has(fields, "clientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.clientLegalRepresentative.residenceAddress, fields["clientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.clientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative")) {
      let secondClientLegalRepresentativeData = pickAndConvert(formData.secondClientLegalRepresentative, fields.secondClientLegalRepresentative);
      secondClientLegalRepresentativeData = sanitizePersonData(secondClientLegalRepresentativeData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.secondClientLegalRepresentative.id,
        data: secondClientLegalRepresentativeData,
      });
    }

    if (_has(fields, "secondClientLegalRepresentative.residenceAddress")) {
      let addressData = pickAndConvert(formData.secondClientLegalRepresentative.residenceAddress, fields["secondClientLegalRepresentative.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.secondClientLegalRepresentative.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization")) {
      let opponentLawFirmData = pickAndConvert(formData.opponentLawFirm.organization, fields["opponentLawFirm.organization"]);
      opponentLawFirmData = sanitizeOrganizationData(opponentLawFirmData);
      opponentLawFirmData.name = opponentLawFirmData.name || (formData.opponentLawyer.familyName ? `Kanzlei ${formData.opponentLawyer.familyName}` : "");
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: formData.opponentLawFirm.organization.id,
        data: opponentLawFirmData,
      });
    }

    if (_has(fields, "opponentLawFirm.organization.mainEmail")) {
      let emailData = pickAndConvert(formData.opponentLawFirm.organization.mainEmail, fields["opponentLawFirm.organization.mainEmail"]);
      emailData = sanitizeOrganizationEmailData(emailData);
      await updateMutation.mutateAsync({
        uri: "/organization_emails",
        id: product.opponentLawFirm.organization.mainEmail.id,
        data: emailData,
      });
    }

    if (_has(fields, "debtCollectionAgency.organization")) {
      let debtCollectionAgencyData = pickAndConvert(formData.debtCollectionAgency.organization, fields["debtCollectionAgency.organization"]);
      debtCollectionAgencyData = sanitizeOrganizationData(debtCollectionAgencyData);
      if (product.debtCollectionAgency === null) {
        product = await updateMutation.mutateAsync({
          uri: getProductData(product.name, "apiUri"),
          id: product.id,
          data: {
            debtCollectionAgency: "/organizations/new",
          },
        });
      }
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: product.debtCollectionAgency.organization.id,
        data: debtCollectionAgencyData,
      });

      if (_has(fields, "debtCollectionAgency.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.debtCollectionAgency.organization.mainEmail, fields["debtCollectionAgency.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.debtCollectionAgency.organization.mainEmail.id,
          data: emailData,
        });
      }

      if (_has(fields, "debtCollectionAgency.organization.address")) {
        let addressData = pickAndConvert(formData.debtCollectionAgency.organization.address, fields["debtCollectionAgency.organization.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.debtCollectionAgency.organization.address.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "client.bankData")) {
      const bankData = pickAndConvert(formData.client.bankData, fields["client.bankData"]);
      if (bankData.iban) {
        bankData.iban = toIban(bankData.iban);
      } else {
        delete bankData.iban;
      }
      if (!_isEmpty(bankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.client.bankData.id,
          data: bankData,
        });
      }
    }

    if (_has(fields, "opponentLawFirm.organization.address")) {
      let opponentLawFirmAddressData = pickAndConvert(formData.opponentLawFirm.organization.address, fields["opponentLawFirm.organization.address"]);
      opponentLawFirmAddressData = sanitizeAddressData(opponentLawFirmAddressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: formData.opponentLawFirm.organization.address.id,
        data: opponentLawFirmAddressData,
      });
    }

    if (_has(fields, "opponentLawyer")) {
      let opponentLawyerData = pickAndConvert(formData.opponentLawyer, fields["opponentLawyer"]);
      opponentLawyerData = sanitizePersonData(opponentLawyerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: formData.opponentLawyer.id,
        data: opponentLawyerData,
      });
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity.organization")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity.organization, fields["opposingPartyLegalEntity.organization"]);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.organization.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.mainEmail")) {
        let emailData = pickAndConvert(formData.opposingPartyLegalEntity.organization.mainEmail, fields["opposingPartyLegalEntity.organization.mainEmail"]);
        emailData = sanitizeOrganizationEmailData(emailData);
        await updateMutation.mutateAsync({
          uri: "/organization_emails",
          id: product.opposingPartyLegalEntity.organization.mainEmail.id,
          data: emailData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.organization.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.organization.address, fields["opposingPartyLegalEntity.organization.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.organization.address.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "caseInsurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let caseInsuranceData = pickAndConvert(convertedFormData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    if (_has(fields, "additionalClients")) {
      await persistAdditionalClients(formData, product, updateMutation, createMutation, deleteMutation);
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, pickAndConvert(formData, fields[product.name]), pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Partial<AbstractCase>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients?.length ? "twoClients" : "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (isFlexServiceProduct(product)) {
      if (!isCurrentFlexServiceProduct(product)) {
        availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY]);
      }

      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    } else {
      const isRecommendationStep = [
        applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION,
        applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION,
      ].includes(formData.applicationStatus);

      if (formData.applicationStatus <= applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY || isRecommendationStep) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_CATEGORY]);
      }
      if (
        (formData.name === "otherContract" && formData.applicationStatus < applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION) ||
        isRecommendationStep
      ) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION]);
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_RECOMMENDATION]);
      }
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && formData.name !== "otherContract") {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]);
      }
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory)) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]);
      }
      if (formData.caseCategory === "defendClaim" && formData.defenseProcessStatus === "debtCollection") {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]);
      }
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
      //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
      // invalidate our next step since formdata updates before the actual request is fired
      if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
      }
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
